import { AccountHoverCard } from '#/components/ethereum/account-hover-card';
import { Timestamp } from '#/components/timestamp';
import { Button } from '#/components/ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '#/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '#/components/ui/collapsible';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '#/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '#/components/ui/tooltip';
import { OrderStatusEnum as EthereumStakingOrderStatusEnum } from '#/constants/order-status';
import { BeaconStatus } from '#/constants/pectra/beacon-status';
import { CredentialTypeEnum } from '#/constants/pectra/credential-type';
import { ValidatorStatusEnum } from '#/constants/validator-status';
import { EthereumStakingOrderInfo } from '#/data/react-query/queries/order-list';
import { ConsolidateDialogContent } from '#/modules/dialogs/consolidate-dialog';
import { DeleteOrderDialogContent } from '#/modules/dialogs/delete-order-dialog';
import { StakingDialogContent } from '#/modules/dialogs/staking-dialog';
import { OrderDisplayName } from '#/modules/display/order-display-name';
import { OrderStatus } from '#/modules/display/order-status';
import { Translation } from 'intl-lib';
import {
  Calendar,
  ChevronDown,
  CreditCard,
  HelpCircle,
  Trash2,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { ReactComponent as ConsolidateIcon } from '../images/consolidate.svg';
import { ReactComponent as EtherIcon } from '../images/ether.svg';
import { ValidatorsTable } from './validators-table';

interface Props {
  item: EthereumStakingOrderInfo;
}

export function OrderRow({ item }: Props) {
  const totalBalance = item.count * item.amount;
  // const totalBalance = item.validators?.reduce(
  //   (acc, current) => acc + Number(current.effectiveBalance),
  //   0
  // );
  const isWaitingForPayment = useMemo(
    () =>
      item.status === EthereumStakingOrderStatusEnum.WAITING_FOR_PAYMENT ||
      item.status === EthereumStakingOrderStatusEnum.PAYMENT_FAILED,
    [item]
  );
  const isWithdrawalable = useMemo(
    () =>
      item.status === EthereumStakingOrderStatusEnum.ACTIVE &&
      item.validators?.every(val => val.status === ValidatorStatusEnum.ACTIVE),
    [item]
  );

  const isActiveOngoing = useMemo(
    () =>
      item.status === EthereumStakingOrderStatusEnum.ACTIVE &&
      item.validators?.some(
        validator =>
          validator.status === ValidatorStatusEnum.ACTIVE && // isActive
          validator.exit != 1 && // not isExiting
          validator.beaconStatus === BeaconStatus.ACTIVE_ONGOING // isActiveOngoing
      ),
    [item]
  );
  const isConsolidatable = useMemo(
    () =>
      item.status === EthereumStakingOrderStatusEnum.ACTIVE &&
      item.validators?.some(
        validator =>
          validator.status === ValidatorStatusEnum.ACTIVE && // isActive
          validator.exit != 1 && // not isExiting
          validator.beaconStatus === BeaconStatus.ACTIVE_ONGOING && // isActiveOngoing
          validator.credentialType == CredentialTypeEnum.TYPE2
      ),
    [item]
  );
  const isCollapsible = useMemo(() => item.validators != null, [item]);
  const [isWithdrawalOpen, setIsWithdrawalOpen] = useState(false);

  const defaultOpen = isActiveOngoing;

  return (
    <>
      <Collapsible defaultOpen={defaultOpen}>
        <Card className="group/order w-full py-5 gap-5 border-none shadow-none">
          <CardHeader className="px-5 gap-4">
            <div className="flex items-center gap-2">
              <CardTitle className="leading-6">
                <OrderDisplayName item={item} />
              </CardTitle>
              <OrderStatus status={item.status} />
            </div>
            <CardDescription>
              <div className="flex items-center gap-x-4 gap-y-2 flex-wrap">
                <div className="flex items-center gap-1 text-sm">
                  <EtherIcon className="size-3.5" />
                  {totalBalance} ETH
                </div>
                <AccountHoverCard
                  address={item?.claimaddr}
                  visibleChars={6}
                  title={
                    <div className="flex items-center gap-1">
                      <h4 className="text-sm font-semibold">
                        <Translation path="EthereumStaking.StakingAction.Information.ClaimAddress.Label" />
                      </h4>
                      <Tooltip>
                        <TooltipTrigger>
                          <HelpCircle className="size-4" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <Translation path="EthereumStaking.StakingAction.Information.ClaimAddress.LabelHelp" />
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  }
                />
                <AccountHoverCard
                  address={item?.withdrawaddr}
                  visibleChars={6}
                  title={
                    <div className="flex items-center gap-1">
                      <h4 className="text-sm font-semibold">
                        <Translation path="EthereumStaking.StakingAction.Information.WithdrawalAddress.Label" />
                      </h4>
                      <Tooltip>
                        <TooltipTrigger>
                          <HelpCircle className="size-4" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <Translation path="EthereumStaking.StakingAction.Information.WithdrawalAddress.LabelHelp" />
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  }
                />
                <div className="flex items-center gap-1">
                  <Calendar className="size-4" />
                  <Timestamp date={item?.createTime} toggleable />
                </div>
              </div>
            </CardDescription>
            <CardAction className="flex items-center gap-2">
              {isActiveOngoing && (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button type="button" variant="outline">
                      <ConsolidateIcon />
                      <Translation path="my_staking.consolidate_dialog.trigger" />
                    </Button>
                  </DialogTrigger>
                  {isConsolidatable ? (
                    <ConsolidateDialogContent originalRow={item} />
                  ) : (
                    <DialogContent className="sm:max-w-sm">
                      <DialogHeader>
                        <DialogTitle>Warning</DialogTitle>
                        <DialogDescription>
                          No 0x02 validator found in this order. Please upgrade
                          one first.
                        </DialogDescription>
                      </DialogHeader>
                      <DialogFooter>
                        <DialogClose asChild>
                          <Button variant="secondary">
                            <Translation path="Application.Dialog.CloseButtonLabel" />
                          </Button>
                        </DialogClose>
                      </DialogFooter>
                    </DialogContent>
                  )}
                </Dialog>
              )}
              {isWaitingForPayment && (
                <>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button type="button" variant="primary">
                        <CreditCard />
                        <Translation path="my_staking.deposit_dialog.trigger" />
                      </Button>
                    </DialogTrigger>
                    <StakingDialogContent originalRow={item} />
                  </Dialog>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button type="button" variant="outline">
                        <Trash2 />
                        <Translation path="my_staking.delete_dialog.trigger" />
                      </Button>
                    </DialogTrigger>
                    <DeleteOrderDialogContent originalRow={item} />
                  </Dialog>
                </>
              )}
              {/* <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon">
                    <MoreHorizontal className="h-4 w-4" />
                    <span className="sr-only">Open menu</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40">
                  <DropdownMenuItem
                    disabled={!isWithdrawalable}
                    // onSelect={tryConnectAndWithdrawal}
                  >
                    <LogOut />
                    <Translation path="my_staking.exit_dialogtrigger" />
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    disabled={!isWaitingForPayment}
                    onSelect={() => setIsDeleteOpen(true)}
                  >
                    <Trash />
                    <Translation path="EthereumStaking.Validators.DeleteAction.ButtonLabel" />
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu> */}
              {isCollapsible && (
                <CollapsibleTrigger asChild className="group">
                  <Button variant="ghost" size="icon">
                    <ChevronDown className="transition-transform group-data-[state=open]:rotate-180" />
                  </Button>
                </CollapsibleTrigger>
              )}
            </CardAction>
          </CardHeader>
          {isCollapsible && (
            <CollapsibleContent asChild>
              <CardContent className="px-5">
                <ValidatorsTable validators={item.validators} />
              </CardContent>
            </CollapsibleContent>
          )}
        </Card>
      </Collapsible>
    </>
  );
}
